import {
  useState,
  useCallback,
  useContext,
  useRef,
  useLayoutEffect,
  useMemo,
  createContext,
  Children,
  isValidElement,
} from "react";
import type { ReactNode, ReactElement } from "react";
import { useInsideDialog } from "../Dialog/Internal/InsideDialogContext";
import type { TabProps } from "./Tab";
import { Tab } from "./Tab";
import {
  TabRegistryContext,
  type TabMeta,
  type TabRegistry,
} from "./Internal/TabRegistryContext";
import { TabPanel_ } from "./Internal/TabPanel";
import { TabStrip_ } from "./Internal/TabStrip";

const TabDepthContext = createContext(0);

interface TabsProps {
  children: ReactNode;
  /** Tighter tab spacing to fit more tabs on screen. */
  dense?: boolean | undefined;
  /** Override the initial active tab (takes precedence over URL param). */
  defaultTab?: string | undefined;
  /**
   * Controls pinned to the right end of the tab strip row — a filter box, a
   * search input, an action that applies to every tab.
   *
   * This is deliberately on `Tabs` and not on `Tab`: `Tab`'s own `filters` /
   * `actions` / `filterBar` slots are per-tab and render inside the panel, below
   * the strip. A filter that applies to all the tabs has nowhere to go there
   * without being declared once per tab, which is why the address book
   * hand-rolled its own tab bar to get a search box onto the row.
   *
   * The strip shrinks to make room, so tabs still collapse into the overflow
   * kebab when space runs out.
   */
  controls?: ReactNode;
  /**
   * Controlled selection. Pass with `onTabChange` when the screen — not Tabs —
   * owns which tab is open, because something outside the tabs needs the answer.
   * The address book is the case: its "Add New Location" button sits in the page
   * header and its ctrl+L shortcut works anywhere, and both mark the new
   * location as a branch when the branches tab is the open one. Neither can ask
   * a Tab which tab it is in.
   *
   * A controlled Tabs leaves the `?tab=` URL param alone: the owner decides
   * whether the selection belongs in the URL.
   */
  activeTab?: string | undefined;
  /** Fired with the id the user picked. Required for `activeTab` to do anything. */
  onTabChange?: ((tabId: string) => void) | undefined;
}

function getUrlParam(depth: number): string {
  return depth === 0 ? "tab" : `tab${depth + 1}`;
}

function readTabFromUrl(paramName: string): string | undefined {
  const params = new URLSearchParams(window.location.search);
  return params.get(paramName) ?? undefined;
}

function writeTabToUrl(paramName: string, tabId: string) {
  const url = new URL(window.location.href);
  url.searchParams.set(paramName, tabId);
  window.history.replaceState({}, "", url.toString());
}

export function Tabs({
  children,
  dense,
  defaultTab,
  controls,
  activeTab,
  onTabChange,
}: TabsProps) {
  const depth = useContext(TabDepthContext);
  const insideDialog = useInsideDialog();
  const syncToUrl = !insideDialog;
  const effectiveDense = dense || insideDialog;

  // Check if all children are direct <Tab> elements (fast path).
  const elements = Children.toArray(children).filter(isValidElement);
  const allDirect = elements.every((el) => el.type === Tab);

  if (allDirect) {
    return (
      <DirectTabs_
        tabs={elements as ReactElement<TabProps>[]}
        dense={effectiveDense}
        depth={depth}
        syncToUrl={syncToUrl}
        defaultTab={defaultTab}
        controls={controls}
        controlledTab={activeTab}
        onControlledChange={onTabChange}
      />
    );
  }

  // Mixed or wrapper-component children: use registry-based rendering.
  return (
    <RegistryTabs_
      dense={effectiveDense}
      depth={depth}
      syncToUrl={syncToUrl}
      defaultTab={defaultTab}
      controls={controls}
      controlledTab={activeTab}
      onControlledChange={onTabChange}
    >
      {children}
    </RegistryTabs_>
  );
}

// ── Direct <Tab> children (original fast path) ──────────────────

// oxlint-disable-next-line complexity -- grandfathered, see ENG-7672
function DirectTabs_({
  tabs,
  dense,
  depth,
  defaultTab,
  syncToUrl,
  controls,
  controlledTab,
  onControlledChange,
}: {
  tabs: ReactElement<TabProps>[];
  dense?: boolean | undefined;
  depth: number;
  syncToUrl: boolean;
  defaultTab?: string | undefined;
  controls?: ReactNode;
  controlledTab?: string | undefined;
  onControlledChange?: ((tabId: string) => void) | undefined;
}) {
  const controlled = controlledTab !== undefined;
  const paramName = getUrlParam(depth);
  const urlTab = syncToUrl ? readTabFromUrl(paramName) : undefined;
  const enabled = tabs.filter((t) => !t.props.disabled);
  const validUrlTab =
    urlTab && enabled.some((t) => t.props.id === urlTab) ? urlTab : undefined;
  const validDefault =
    defaultTab && enabled.some((t) => t.props.id === defaultTab)
      ? defaultTab
      : undefined;

  // A ?tab= in the URL is someone's explicit choice (a shared link, a reload),
  // so it wins over the owner's defaultTab, which only fills the gap when the
  // URL says nothing.
  const initialTab =
    validUrlTab ??
    validDefault ??
    enabled[0]?.props.id ??
    tabs[0]?.props.id ??
    "";

  const [internalTab, setInternalTab] = useState(initialTab);
  const activeTab = controlled ? controlledTab : internalTab;

  const handleTabChange = useCallback(
    (tabId: string) => {
      onControlledChange?.(tabId);
      if (controlled) {
        return;
      }
      setInternalTab(tabId);
      if (syncToUrl) {
        writeTabToUrl(paramName, tabId);
      }
    },
    [controlled, onControlledChange, syncToUrl, paramName],
  );

  const tabMetas = tabs.map((t) => ({
    id: t.props.id,
    title: t.props.title,
    testId: t.props.testId,
    count: t.props.count,
    countColor: t.props.countColor,
    disabled: t.props.disabled,
    icon: t.props.icon,
    warning: t.props.warning,
  }));

  // The selected tab can disappear from `tabs` when the parent re-filters
  // its content (e.g. a search empties that bucket). Fall back to the first
  // available tab so the panel never renders blank. The stored selection is
  // left untouched, so it's restored if the tab reappears (filter cleared).
  const active =
    tabs.find((t) => t.props.id === activeTab) ?? enabled[0] ?? tabs[0];
  const displayTab = active?.props.id ?? activeTab;

  return (
    <TabDepthContext.Provider value={depth + 1}>
      <div className={depth === 0 ? "pt-4" : ""}>
        <TabStrip_
          tabs={tabMetas}
          activeTab={displayTab}
          onTabChange={handleTabChange}
          dense={dense}
          controls={controls}
        />
        {active && <TabPanel_ tabProps={active.props} />}
      </div>
    </TabDepthContext.Provider>
  );
}

// ── Registry-based rendering (wrapper components) ───────────────
//
// When children are wrapper components that render <Tab> internally,
// we use a context-based registry. Each <Tab> registers metadata into
// a shared ref during render.
//
// The rendering uses CSS flexbox `order` so that:
// - Children appear first in the React element tree (depth-first
//   traversal ensures Tab registrations populate metaRef before the
//   header reads it).
// - CSS `order-first` on the header displays it above the content.
//
// On the initial render, activeTab is "" so no Tab renders content
// (no visible flash). A useLayoutEffect immediately resolves the
// initial tab and triggers a synchronous re-render before paint.

function RegistryTabs_({
  children,
  dense,
  depth,
  syncToUrl,
  defaultTab,
  controls,
  controlledTab,
  onControlledChange,
}: {
  children: ReactNode;
  dense?: boolean | undefined;
  depth: number;
  syncToUrl: boolean;
  defaultTab?: string | undefined;
  controls?: ReactNode;
  controlledTab?: string | undefined;
  onControlledChange?: ((tabId: string) => void) | undefined;
}) {
  const controlled = controlledTab !== undefined;
  const paramName = getUrlParam(depth);
  const urlTab = syncToUrl ? readTabFromUrl(paramName) : undefined;
  const metaRef = useRef<TabMeta[]>([]);
  const [internalTab, setInternalTab] = useState("");
  const activeTab = controlled ? controlledTab : internalTab;

  const handleTabChange = useCallback(
    (tabId: string) => {
      onControlledChange?.(tabId);
      if (controlled) {
        return;
      }
      setInternalTab(tabId);
      if (syncToUrl) {
        writeTabToUrl(paramName, tabId);
      }
    },
    [controlled, onControlledChange, syncToUrl, paramName],
  );

  // Clear registrations each render so tabs re-register with fresh props.
  metaRef.current = [];

  const registry: TabRegistry = useMemo(
    () => ({ metaRef, activeId: activeTab }),
    [activeTab],
  );

  // After the first render, resolve the initial active tab from
  // registered metadata. useLayoutEffect runs before paint, so the
  // user never sees an empty state.
  // oxlint-disable-next-line complexity -- grandfathered, see ENG-7672
  useLayoutEffect(() => {
    // A controlled owner picks the tab; resolving one here would fight it.
    if (controlled) {
      return;
    }
    const tabs = metaRef.current;
    if (tabs.length === 0) {
      return;
    }
    // Re-resolve the active tab when it's unset OR when it no longer maps to
    // a registered tab — the latter happens when the parent re-filters and
    // the previously-active bucket empties out. Without this the registry
    // renders no panel at all (every Tab bails on the id mismatch).
    const activeStillAvailable = tabs.some((t) => t.id === activeTab);
    if (activeTab && activeStillAvailable) {
      return;
    }
    const enabled = tabs.filter((t) => !t.disabled);
    const validUrlTab =
      urlTab && enabled.some((t) => t.id === urlTab) ? urlTab : undefined;
    const validDefault =
      defaultTab && enabled.some((t) => t.id === defaultTab)
        ? defaultTab
        : undefined;
    // Same precedence as the direct path: the URL's explicit tab beats the
    // owner's defaultTab.
    const firstTab =
      validUrlTab ?? validDefault ?? enabled[0]?.id ?? tabs[0]?.id ?? "";
    if (firstTab && firstTab !== activeTab) {
      setInternalTab(firstTab);
    }
  });

  return (
    <TabDepthContext.Provider value={depth + 1}>
      <TabRegistryContext.Provider value={registry}>
        <div className={`flex flex-col ${depth === 0 ? "pt-4" : ""}`}>
          {/* Children render first in depth-first traversal, populating
              metaRef. Active <Tab> renders its own content panel. */}
          {children}
          {/* Header renders after children, reading populated metaRef.
              CSS order-first puts it visually above the content. */}
          <RegistryTabHeader_
            metaRef={metaRef}
            activeTab={activeTab}
            onTabChange={handleTabChange}
            dense={dense}
            controls={controls}
          />
        </div>
      </TabRegistryContext.Provider>
    </TabDepthContext.Provider>
  );
}

/**
 * Renders the tab triggers strip. Appears after children in the React
 * tree (so metaRef is populated), but uses CSS order-first to display
 * above the content.
 */
function RegistryTabHeader_({
  metaRef,
  activeTab,
  onTabChange,
  dense,
  controls,
}: {
  metaRef: React.RefObject<TabMeta[]>;
  activeTab: string;
  onTabChange: (id: string) => void;
  dense?: boolean | undefined;
  controls?: ReactNode;
}) {
  const tabs = metaRef.current;

  // On the initial render (activeTab = ""), show the first tab as
  // visually active in the trigger strip even though content hasn't
  // rendered yet. useLayoutEffect in the parent will immediately set
  // the real activeTab before paint.
  const displayActive = activeTab || tabs[0]?.id || "";

  return (
    <div className="order-first">
      <TabStrip_
        tabs={tabs}
        activeTab={displayActive}
        onTabChange={onTabChange}
        dense={dense}
        controls={controls}
      />
    </div>
  );
}
